import { useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiInputSelectBox } from "@miplatform/components";
import { formatDate } from "@miplatform/utils";
import { Box, Typography } from "@mui/material";

const CURRENT_ISSUE_ATTEMPT_VALUE = "non-existing";

export const IssueHistoryHeader = ({
  attemptNumber = 0,
  currentIssue = {},
  issueResult,
  navigate = () => {},
}) => {
  const [selectedIssueId, setSelectedIssueId] = useState(
    CURRENT_ISSUE_ATTEMPT_VALUE
  );
  const { classes, theme } = useStyles({
    isCurrentIssue: selectedIssueId === CURRENT_ISSUE_ATTEMPT_VALUE,
  });

  const options = (currentIssue?.issueResults || [])
    .filter((x) => x.attemptNumber !== attemptNumber)
    .map((x) => ({
      value: x?.id,
      label: `View Attempt #${x?.attemptNumber} Notes`,
      no: x.attemptNumber,
    }));

  options.push({
    value: CURRENT_ISSUE_ATTEMPT_VALUE,
    label: `Current Attempt`,
    color: theme.palette.primary.main,
    no: attemptNumber,
  });

  const maxNumberOfAttempts = Math.max(
    ...(currentIssue.issueResults || []).map((r) => r.attemptNumber),
    attemptNumber
  );

  const handleSelect = (e) => {
    setSelectedIssueId(e.target.value);
    const i = currentIssue.issueResults.find((x) => x.id === e.target.value);
    navigate(i);
  };

  return (
    <Box className={classes.issueHistoryHeader}>
      <Box className={classes.leftBox}>
        <Typography width="100%" fontSize={theme.fontSize.medium}>
          {`Attempt ${
            issueResult?.attemptNumber || attemptNumber
          } of ${maxNumberOfAttempts} - ${
            issueResult ? "Read Only" : "Current"
          }`}
        </Typography>
        <MiInputSelectBox
          disableNone
          variant="outlined"
          options={options.sort((a, b) => a.no - b.no)}
          sx={{
            width: 150,
            marginLeft: "auto",
          }}
          handleChange={handleSelect}
          value={selectedIssueId}
          menuItemStyle={{
            fontSize: theme.fontSize.xsmall,
            backgroundColor: theme.palette.common.white,
            padding: theme.spacing(0.5, 1.5),
            border: `1px solid ${theme.palette.common.black}`,
            fontWeight: theme.typography.fontWeightBold,
            "& .MuiMenu-list": {
              backgroundColor: "#fff",
            },
            "&:hover": {
              backgroundColor: "#E0F7FA",
            },
          }}
          InputProps={{
            sx: {
              height: 20,
              fontWeight: theme.fontWeight.bold,
              backgroundColor: theme.palette.common.white,
              borderRadius: 0,
              border: `0.5px solid ${theme.palette.grey[800]}`,
              fontSize: theme.fontSize.xsmall,
              "& .MuiOutlinedInput-input": {
                padding: 0,
                paddingLeft: theme.spacing(1.5),
              },
            },
          }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  paddingTop: 0,
                  paddingBottom: 0,
                  borderRadius: 0,
                  "& .MuiMenu-list": {
                    paddingTop: 0,
                    paddingBottom: 0,
                  },
                },
              },
            },
          }}
        />
      </Box>
      <Box className={classes.rightBox}>
        Last Updated:{" "}
        {formatDate(
          issueResult?.modifiedOn || currentIssue?.modifiedOn,
          "MM-DD-YYYY hh:mm A"
        )}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme, { isCurrentIssue }) => {
  const borderColor = isCurrentIssue
    ? theme.palette.primary.medium
    : theme.palette.grey.medium;
  const bgColor = isCurrentIssue
    ? theme.palette.primary.dark
    : theme.palette.grey.darkerAccent;
  const secondaryBgColor = isCurrentIssue
    ? theme.palette.primary.main
    : theme.palette.grey.darkAccent;
  return {
    issueHistoryHeader: {
      fontWeight: theme.typography.medium,
      color: theme.palette.common.white,
      fontSize: theme.fontSize.medium,
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      height: 40,
      minHeight: 40,
      backgroundColor: bgColor,
      borderRadius: theme.spacing(0.5, 0.5, 0, 0),
      overflow: "hidden",
    },
    leftBox: {
      width: "47%",
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      height: "100%",
      padding: theme.spacing(2),
      fontSize: theme.fontSize.medium,
      border: `0.25px solid ${borderColor}`,
    },
    attemptButton: {
      backgroundColor: theme.palette.common.white,
      color: borderColor,
      border: `1px solid ${borderColor}`,
      height: "fit-content",
      display: "flex",
      alignItems: "center",
      fontWeight: theme.fontWeight.bold,
      fontSize: theme.fontSize.xsmall,
      ":hover": {
        cursor: "pointer",
      },
    },
    icon: {
      padding: 0,
    },
    rightBox: {
      display: "flex",
      width: "55%",
      backgroundColor: secondaryBgColor,
      fontWeight: theme.fontWeight.regular,
      justifyContent: "flex-end",
      alignItems: "center",
      height: "100%",
      paddingRight: theme.spacing(2),
      border: `0.25px solid ${borderColor}`,
    },
  };
});
